(function () {
  const GRID_ID = 'ProductGridContainer';
  const SWAP_IDS = [
    'ProductGridContainer',
    'ProductCount',
    'SortByContainer',
    'SortByContainerMobile',
    'FacetFiltersActive',
  ];
  const TAB_SELECTOR = 'a.material-tabs__tab';

  class MaterialTabs extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    get grid() {
      return document.getElementById(GRID_ID);
    }

    // The product grid lives in its own section. Read its id at runtime rather
    // than hardcoding it, so this keeps working if the template is rebuilt.
    get sectionId() {
      const grid = this.grid;
      if (!grid) return null;
      const section = grid.closest('.shopify-section');
      if (!section || !section.id) return null;
      return section.id.replace('shopify-section-', '');
    }

    canEnhance() {
      return Boolean(this.grid && this.sectionId && window.history && window.DOMParser && window.fetch);
    }

    pathOf(url) {
      return new URL(url, window.location.origin).pathname;
    }

    tabs() {
      return Array.from(this.querySelectorAll(TAB_SELECTOR));
    }

    onClick(event) {
      const link = event.target.closest(TAB_SELECTOR);
      if (!link) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      if (link.classList.contains('is-current')) {
        event.preventDefault();
        return;
      }

      // Without the pieces we need, leave the link alone and let it navigate.
      if (!this.canEnhance()) return;

      event.preventDefault();
      const url = this.withSortOrder(link.href);
      history.pushState({ materialTabsUrl: url }, '', url);
      this.navigate(url, true);
    }

    // A tab narrows down the same products, so keep the shopper's sort order.
    withSortOrder(href) {
      const sortBy = new URLSearchParams(window.location.search).get('sort_by');
      if (!sortBy) return href;
      const url = new URL(href, window.location.origin);
      url.searchParams.set('sort_by', sortBy);
      return url.toString();
    }

    onPopState() {
      if (!this.canEnhance()) return;
      // Only take over when the new entry is one of our own tabs.
      const path = window.location.pathname;
      if (!this.tabs().some((tab) => this.pathOf(tab.href) === path)) return;
      this.navigate(window.location.href, false);
    }

    navigate(url, scroll) {
      this.setActive(url);
      this.setBusy(true);

      this.fetchSection(url)
        .then((html) => {
          this.render(html);
          if (scroll) this.maybeScroll();
          this.setBusy(false);
        })
        .catch(() => {
          // Anything goes wrong and we fall back to a normal page load.
          window.location.href = url;
        });
    }

    fetchSection(url) {
      if (this.cache.has(url)) return Promise.resolve(this.cache.get(url));

      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      return fetch(fetchUrl.toString())
        .then((response) => {
          if (!response.ok) throw new Error('Section request failed: ' + response.status);
          return response.text();
        })
        .then((text) => {
          this.cache.set(url, text);
          return text;
        });
    }

    render(responseText) {
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');

      SWAP_IDS.forEach((id) => {
        const current = document.getElementById(id);
        const next = parsed.getElementById(id);
        if (current && next) current.innerHTML = next.innerHTML;
      });

      const layoutSwitcher = document.querySelector('#ProductGridWrapper layout-switcher');
      if (layoutSwitcher && typeof layoutSwitcher.onButtonClick === 'function') {
        const active = layoutSwitcher.querySelector('.btn--active');
        if (active) layoutSwitcher.onButtonClick(active);
      }

      // Same signal the theme's own filtering sends, so anything listening re-runs.
      document.dispatchEvent(new CustomEvent('collection:rerendered'));
    }

    setActive(url) {
      const path = this.pathOf(url);
      this.tabs().forEach((tab) => {
        const isCurrent = this.pathOf(tab.href) === path;
        tab.classList.toggle('is-current', isCurrent);
        if (isCurrent) {
          tab.setAttribute('aria-current', 'page');
        } else {
          tab.removeAttribute('aria-current');
        }
      });
    }

    setBusy(state) {
      const grid = this.grid;
      if (!grid) return;
      grid.classList.toggle('material-tabs-loading', state);
      grid.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    // Only pull the page up when the tabs have scrolled off the top.
    maybeScroll() {
      const top = this.getBoundingClientRect().top;
      if (top >= 0) return;
      const reduced =
        (window.FoxTheme && FoxTheme.config && FoxTheme.config.motionReduced) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: top + window.scrollY - 95,
        behavior: reduced ? 'auto' : 'smooth',
      });
    }
  }

  if (!customElements.get('material-tabs')) {
    customElements.define('material-tabs', MaterialTabs);
  }
})();
